import { useEffect, useRef } from 'react';
import { Check, Flag, Lock, Star, StickyNote } from 'lucide-react';
import type { InboxNote, InboxStatus, InboxThread } from '../../types/inbox';
import type { CurrentUser } from '../../types/user';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { SelectField } from '../../components/ui/SelectField';
import { cn } from '../../lib/utils';
import { formatRelativeTime } from '../../lib/relativeTime';
import { CHANNEL_LABEL, formatExactTimestamp } from '../../lib/inboxView';
import { ChannelIcon } from '../../components/ui/ChannelIcon';
import { claimThread, releaseThreadClaim } from './api';
import type { AccountExternalLinks } from '../../types/account';
import { AccountLinkIcons } from '../../components/ui/AccountLinkIcons';

// Community Inbox Phase 2 — presentational + behaviour primitives shared by the
// Review and DM detail bodies (the comment body keeps its own inline copies).

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'pending', label: 'Pending' },
  { value: 'resolved', label: 'Resolved' },
];

/** Priority flag toggle (Part A) — shared by every detail header. */
export function PriorityToggle({
  priority,
  onToggle,
  disabled,
}: {
  priority: boolean;
  onToggle: () => void;
  disabled?: boolean;
}) {
  return (
    <Button
      variant="outline"
      size="sm"
      className={cn('h-7', priority && 'border-amber-300 bg-amber-50 text-amber-700')}
      title={priority ? 'Clear priority' : 'Flag as priority'}
      disabled={disabled}
      onClick={onToggle}
    >
      <Flag className={cn('h-3.5 w-3.5', priority && 'fill-amber-500 text-amber-600')} />
      {priority ? 'Priority' : 'Flag'}
    </Button>
  );
}

export function StarRating({ rating, className }: { rating: number | null | undefined; className?: string }) {
  if (typeof rating !== 'number') return null;
  const rounded = Math.round(rating);
  return (
    <span className={cn('inline-flex items-center gap-0.5', className)} title={`${rating} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={cn(
            'h-3.5 w-3.5',
            n <= rounded ? 'fill-amber-400 text-amber-400' : 'text-slate-300',
          )}
        />
      ))}
    </span>
  );
}

/** Header row: channel icon + author + a per-kind extra slot + resolve button. */
export function DetailHeader({
  thread,
  accountNameById,
  accountLinks,
  extra,
  onResolve,
  resolving,
  children,
}: {
  thread: InboxThread;
  accountNameById: Map<string, string>;
  // The account's external link icons; undefined/empty renders nothing.
  accountLinks: AccountExternalLinks | undefined;
  extra?: React.ReactNode;
  onResolve: () => void;
  resolving: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className="shrink-0 border-b border-slate-200 bg-slate-50 px-4 py-3">
      <div className="flex items-center gap-2">
        <ChannelIcon channel={thread.channel} />
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900">
          {thread.author.name}
        </span>
        {extra}
        {thread.status !== 'resolved' && (
          <Button
            variant="outline"
            size="sm"
            className="h-7"
            title="Mark resolved"
            disabled={resolving}
            onClick={onResolve}
          >
            <Check className="h-3.5 w-3.5 text-kyck-600" />
            Resolve
          </Button>
        )}
      </div>
      <div className="mt-0.5 flex flex-wrap items-center gap-2">
        <p className="text-xs text-slate-500">
          {accountNameById.get(thread.kyckAccountId) ?? thread.kyckAccountId} ·{' '}
          {CHANNEL_LABEL[thread.channel]}
        </p>
        <AccountLinkIcons links={accountLinks} />
      </div>
      {children}
    </div>
  );
}

/** Status + assignee selects (audited on change via the passed handlers). */
export function WorkflowSelects({
  thread,
  users,
  onStatus,
  onAssign,
}: {
  thread: InboxThread;
  users: CurrentUser[];
  onStatus: (status: InboxStatus) => void;
  onAssign: (uid: string | null) => void;
}) {
  const assigneeOptions = [
    { value: '', label: 'Unassigned' },
    ...users.map((u) => ({ value: u.uid, label: u.displayName || u.email })),
  ];
  return (
    <div className="mt-2 flex items-center gap-2">
      <SelectField
        size="sm"
        aria-label="Status"
        value={thread.status}
        options={STATUS_OPTIONS}
        onValueChange={(v) => onStatus(v as InboxStatus)}
      />
      <SelectField
        size="sm"
        aria-label="Assignee"
        value={thread.assigneeUid ?? ''}
        options={assigneeOptions}
        placeholder="Assign…"
        onValueChange={(v) => onAssign(v === '' ? null : v)}
      />
    </div>
  );
}

export function CollisionBanner({ label }: { label: string }) {
  return (
    <div className="flex shrink-0 items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
      <Lock className="h-3.5 w-3.5" />
      {label} is replying…
    </div>
  );
}

/** Collapsible internal-notes panel + the toggle button (rendered separately). */
export function NotesToggle({
  count,
  onToggle,
}: {
  count: number;
  onToggle: () => void;
}) {
  return (
    <Button variant="ghost" size="sm" onClick={onToggle} title="Internal notes (never sent to the customer)">
      <StickyNote className="h-3.5 w-3.5" />
      Notes ({count})
    </Button>
  );
}

export function NotesPanel({
  notes,
  draft,
  setDraft,
  onAdd,
  adding,
}: {
  notes: InboxNote[];
  draft: string;
  setDraft: (v: string) => void;
  onAdd: () => void;
  adding: boolean;
}) {
  return (
    <div className="shrink-0 border-t border-slate-200 bg-amber-50/40 px-4 py-3">
      <div className="scroll-subtle max-h-36 space-y-2 overflow-y-auto">
        {notes.map((n) => (
          <div key={n.id} className="rounded-md bg-amber-50 px-2.5 py-1.5 text-sm text-slate-700">
            <span className="font-medium">{n.authorName}</span>{' '}
            <span className="text-xs text-slate-400" title={formatExactTimestamp(n.createdAt)}>
              {formatRelativeTime(n.createdAt)}
            </span>
            <p className="whitespace-pre-wrap">{n.text}</p>
          </div>
        ))}
        {notes.length === 0 && <p className="text-xs text-slate-500">No internal notes yet.</p>}
      </div>
      <div className="mt-2 flex items-start gap-2">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add an internal note (never sent to the customer)…"
          rows={1}
          className="min-h-9"
        />
        <Button variant="outline" size="sm" onClick={onAdd} disabled={draft.trim() === '' || adding}>
          Add
        </Button>
      </div>
    </div>
  );
}

/**
 * Claim-on-focus / release-on-unmount, shared by the review + DM composers. The
 * claim is a doc-only 120s soft-lock so a colleague sees "X is replying".
 */
export function useComposerClaim(threadId: string) {
  const claimHeld = useRef(false);
  useEffect(() => {
    return () => {
      if (claimHeld.current) {
        claimHeld.current = false;
        void releaseThreadClaim(threadId).catch(() => undefined);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);
  const onFocus = () => {
    if (claimHeld.current) return;
    void claimThread(threadId)
      .then((res) => {
        claimHeld.current = res.claimed;
      })
      .catch(() => undefined);
  };
  const release = () => {
    claimHeld.current = false;
  };
  return { onFocus, release };
}

export function HiddenBadge() {
  return <Badge variant="outline">Hidden</Badge>;
}
